import { useOktaAuth } from '@okta/okta-react';
import React, { FC, memo, useEffect } from 'react';
import { useHistory, useLocation } from 'react-router';
import { UserType } from 'api/models';
import { Loading, PageLoading } from 'components/atoms/loading';
import { MentionService } from 'components/atoms/mention-service';
import { TaskPaneHashRewrite } from 'components/atoms/task-pane-hash-rewrite';
import { MagicLinkMessageType } from 'components/molecules/session-setup/magic-link-generate';
import { CommandBar } from 'components/organisms/commandbar/cmdk';
import { NotificationsDrawer } from 'components/organisms/notifications/drawer/notifications-drawer';
import { PreviewWrapper } from 'components/organisms/projects/project-preview/preview';
import { TaskDrawer } from 'components/organisms/task-drawer';
import { InitialAppFetch } from 'components/pages/initial-app-fetch';
import { RoutePaths } from 'constant';
import { useCustomerPreviewForCustomerPortalV2 } from 'contexts/providers';
import { NotificationsDrawerContextProvider } from 'contexts/providers/notifications-drawer';
import { ProjectSpacesRearrangeModalContextProvider } from 'contexts/providers/rearrange-modal';
import { TaskPaneContextProvider } from 'contexts/providers/task-pane';
import { useAccount } from 'hooks/use-account';
import { useRouteLoadEventInitialiser } from 'hooks/use-data-dog-logger';
import {
  clearColumnOrderTableFromIndexedDb,
  useInitIndexDbListeners,
} from 'hooks/use-idb';
import { useQueryParams } from 'hooks/use-query-params';
import { useUser } from 'hooks/use-user';
import { ActiveTab } from 'services';
import { unreachable } from 'utils/unreachable';
import { LazyBootApp } from './app-boot-data';
import { PortalBotRoutes } from './customer/portal-bot-routes';

const CustomerRoutes = React.lazy(() => import('./customer/customer'));

const VendorRoutes = React.lazy(() => import('./vendor'));

const CLEAR_IDB_LOCALSTORAGE_KEY = '__clear_idb_502';

const Cerebrum = () => {
  const {
    user: { userType },
  } = useUser();
  const { accountId } = useAccount();
  const { isLoading, isPreviewAvailable } =
    useCustomerPreviewForCustomerPortalV2();

  useEffect(() => {
    async function clearIndexedDBForPosist() {
      const apiKey = localStorage.getItem('__api_key');
      const flag = localStorage.getItem(CLEAR_IDB_LOCALSTORAGE_KEY);

      if (!flag && accountId === 502) {
        await clearColumnOrderTableFromIndexedDb();

        localStorage.clear();
        localStorage.setItem(
          CLEAR_IDB_LOCALSTORAGE_KEY,
          JSON.stringify(new Date().getTime()),
        );
        if (apiKey) {
          localStorage.setItem('__api_key', apiKey);
        }
        window.location.reload();
      }
    }
    clearIndexedDBForPosist();
  }, [accountId]);

  if (isLoading) {
    return <Loading />;
  }

  if (isPreviewAvailable) {
    return <CustomerRoutes />;
  }

  switch (userType) {
    case UserType.External:
      return <CustomerRoutes />;
    case UserType.CustomerPortalBot:
      return <PortalBotRoutes />;
    case UserType.Native:
    case UserType.Partner:
      return <VendorRoutes />;
    default:
      return unreachable(userType);
  }
};

const MemoisedCerbrum = memo(() => {
  return (
    <>
      <Cerebrum />
      <MentionService />
    </>
  );
});

enum UserTypeQueryParamValue {
  Customer = '2',
}
const USER_TYPE_QUERY_PARAM_KEY = 'ut';
const TOKEN_QUERY_PARAM_KEY = 'token';

export const ProtectedRoutes: FC = () => {
  const { authState } = useOktaAuth();
  const { push, replace } = useHistory();
  const params = useQueryParams();
  const location = useLocation();
  const { hash, state } = location;

  useInitIndexDbListeners();
  useRouteLoadEventInitialiser();

  useEffect(() => {
    if (!authState?.isAuthenticated) {
      ActiveTab.save();
    }
  }, [authState]);

  useEffect(() => {
    if (authState && authState.isAuthenticated) {
      const isCustomerViewTypeParamPresent =
        params.get(USER_TYPE_QUERY_PARAM_KEY) ===
        UserTypeQueryParamValue.Customer;
      const isTokenParamPresent = params.has(TOKEN_QUERY_PARAM_KEY);

      if (isCustomerViewTypeParamPresent || isTokenParamPresent) {
        if (isCustomerViewTypeParamPresent) {
          params.delete(USER_TYPE_QUERY_PARAM_KEY);
        }
        if (isTokenParamPresent) {
          params.delete(TOKEN_QUERY_PARAM_KEY);
        }
        replace({
          ...location,
          search: params.toString(),
        });
      }
    }
  }, [authState, params, replace, location]);

  useEffect(() => {
    if (!authState || !authState.isAuthenticated) {
      /* NOTE: if the url consist of ut=2 which is customer type and the user is not authenticated, then we need to take them to magic link page */
      if (
        params.get(USER_TYPE_QUERY_PARAM_KEY) ===
        UserTypeQueryParamValue.Customer
      ) {
        if (params.has(TOKEN_QUERY_PARAM_KEY)) {
          push({
            pathname: RoutePaths.magicLink,
            search: params.toString(),
            hash,
            state: {
              ...state,
              messageType: MagicLinkMessageType.NotSignedIn,
            },
          });
        } else {
          push({
            pathname: RoutePaths.newMagicLink,
            search: params.toString(),
            hash,
            state: {
              ...state,
              messageType: MagicLinkMessageType.NotSignedIn,
            },
          });
        }
      } else {
        push({ pathname: RoutePaths.login, search: params.toString(), hash });
      }
    }
  }, [authState, push, params, hash, state]);

  return !authState ? (
    <PageLoading size="large" />
  ) : (
    <>
      <PreviewWrapper>
        <LazyBootApp>
          <TaskPaneContextProvider>
            <NotificationsDrawerContextProvider>
              <ProjectSpacesRearrangeModalContextProvider>
                <CommandBar />
                <MemoisedCerbrum />
                <TaskDrawer />
                <NotificationsDrawer />
                <TaskPaneHashRewrite />
                <InitialAppFetch />
              </ProjectSpacesRearrangeModalContextProvider>
            </NotificationsDrawerContextProvider>
          </TaskPaneContextProvider>
        </LazyBootApp>
      </PreviewWrapper>
    </>
  );
};
